import React, { useContext, useEffect, useState } from "react";
import { DataQuality, DataQualityLegend, FilterArea, KpiBenchmarkLegend, KpiScoreCard } from "@sia/style-guide";
import { usePageRefresh } from "../tools";
import { getKeyFiguresCurrency, getKeyFiguresPageQuality, parseKpiScoreCardData } from "./data/dataParsing";
import {
  formatKpiBadgeDifference,
  formatKpiBadgeValue,
  formatKpiScaleValue,
  getKpiUnitLabel,
  isKpiDifferenceRoundedToZero,
} from "./data/kpiFormatters";
import { MasterKeyContext } from "contexts/MasterKeyContext";
import { KPI_SCORECARD_TRANSLATION_KEYS } from "./kpiConfiguration";
import "./KeyFiguresPage.scss";
export default function KeyFiguresPage({ translate, filters, setFilters, data, selectedTab, setSelectedTab }) {
  const KEYFIGURES_PAGE_TAB_INDEX = 3;
  usePageRefresh(selectedTab, KEYFIGURES_PAGE_TAB_INDEX, setSelectedTab);

  const { isMasterKeyValid } = useContext(MasterKeyContext);
  const kpiCards = parseKpiScoreCardData(data, translate);
  const currency = getKeyFiguresCurrency(data);
  const canDisplayOwnData = Boolean(isMasterKeyValid) && kpiCards.some((kpi) => kpi.respondentValue !== null);
  const [displayOwnData, setDisplayOwnData] = useState(canDisplayOwnData);

  useEffect(() => {
    setDisplayOwnData(canDisplayOwnData);
  }, [canDisplayOwnData]);

  const showOwnData = canDisplayOwnData && displayOwnData;
  const pageQuality = getKeyFiguresPageQuality(kpiCards);
  const classificationLabels = {
    "below-fair": translate(KPI_SCORECARD_TRANSLATION_KEYS.legend.belowFair),
    fair: translate(KPI_SCORECARD_TRANSLATION_KEYS.legend.fair),
    good: translate(KPI_SCORECARD_TRANSLATION_KEYS.legend.good),
    "very-good": translate(KPI_SCORECARD_TRANSLATION_KEYS.legend.veryGood),
  };

  return (
    <div className="benchmarking-key-container sia-padding-02-left sia-padding-02-right">
      <p className="key-figures-title sia-text-15 sia-padding-06-top sia-padding-06-bottom">
        {translate("benchmarking.evaluations.key-figures.title")}
      </p>
      {filters && (
        <FilterArea
          translate={translate}
          initialFilters={filters}
          onFiltersUpdate={(filterAreaState) => {
            setFilters((state) => ({ ...state, filters: filterAreaState, newest: true }));
          }}
        />
      )}
      {pageQuality && (
        <div className="sia-key-figures-page-quality sia-padding-05-bottom">
          <DataQuality
            translate={translate}
            numberOfRecords={pageQuality.count}
            coefficient={pageQuality.coefficientOfVariation}
            label="benchmarking.components.dataquality.number-of-records"
          />
        </div>
      )}
      <div className="sia-key-figures-scorecards__controls sia-padding-05-top">
        <label className={`sia-key-figures-display-data ${canDisplayOwnData ? "" : "is-disabled"}`}>
          <input type="checkbox" checked={showOwnData} disabled={!canDisplayOwnData} onChange={(event) => setDisplayOwnData(event.target.checked)} />
          <span>{translate(KPI_SCORECARD_TRANSLATION_KEYS.displayMyData)}</span>
        </label>
        <KpiBenchmarkLegend
          label={translate(KPI_SCORECARD_TRANSLATION_KEYS.legend.label)}
          title={translate(KPI_SCORECARD_TRANSLATION_KEYS.legend.title)}
          description={translate(KPI_SCORECARD_TRANSLATION_KEYS.legend.description)}
          normalDistributionLabel={translate(KPI_SCORECARD_TRANSLATION_KEYS.legend.normalDistribution)}
          meanLabel={translate(KPI_SCORECARD_TRANSLATION_KEYS.legend.mean)}
          sdLabel={translate(KPI_SCORECARD_TRANSLATION_KEYS.legend.sd)}
          higherLabel={translate(KPI_SCORECARD_TRANSLATION_KEYS.legend.higher)}
          lowerLabel={translate(KPI_SCORECARD_TRANSLATION_KEYS.legend.lower)}
          closeLabel={translate(KPI_SCORECARD_TRANSLATION_KEYS.legend.close)}
          labels={{
            belowFair: classificationLabels["below-fair"],
            fair: classificationLabels.fair,
            good: classificationLabels.good,
            veryGood: classificationLabels["very-good"],
          }}
        />
      </div>
      <section className="sia-key-figures-scorecards">
        <div className="sia-key-figures-scorecards__header">
          <h3>{translate(KPI_SCORECARD_TRANSLATION_KEYS.sectionTitle)}</h3>
        </div>
        {kpiCards.length === 0 && (
          <p role="status">{translate(data == null ? KPI_SCORECARD_TRANSLATION_KEYS.loading : KPI_SCORECARD_TRANSLATION_KEYS.empty)}</p>
        )}
        <div className="sia-key-figures-scorecards__grid">
          {kpiCards.map((kpi) => (
            <KpiScoreCard
              key={`${kpi.id}-${kpi.period || "latest"}`}
              title={kpi.title}
              formulaLabel={translate(KPI_SCORECARD_TRANSLATION_KEYS.formulaLabel)}
              interpretationLabel={translate(KPI_SCORECARD_TRANSLATION_KEYS.interpretationLabel)}
              formula={kpi.formula}
              unitLabel={getKpiUnitLabel(kpi.unit, currency)}
              interpretation={kpi.interpretation}
              mean={kpi.mean}
              sd={kpi.sd}
              respondentValue={kpi.respondentValue}
              direction={kpi.direction}
              showRespondent={showOwnData}
              formatValue={(value) => formatKpiBadgeValue(value, kpi.unit)}
              formatScaleValue={(value) => formatKpiScaleValue(value, kpi.unit, currency)}
              formatDifference={(value) => formatKpiBadgeDifference(value, kpi.unit)}
              isDifferenceZero={(value) => isKpiDifferenceRoundedToZero(value, kpi.unit)}
              classificationLabels={classificationLabels}
            />
          ))}
        </div>
      </section>
      <DataQualityLegend translate={translate} />
    </div>
  );
}
